import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import useHttpClient from './useHttpClient';
import IContact from './models/contact';

interface IOrgContacts {
  count: number;
  offset: number;
  limit: number;
  hasMore: boolean;
  requestedFields: string;
  items: IContact[];
}

export interface IContactsPayload {
  loading: boolean;
  error?: Error;
  contacts?: IOrgContacts;
  called: boolean;
}

export interface IQueryContactsHook {
  contactsSearchResults: IContactsPayload;
  getContacts: (
    searchTerm: string,
    limit?: number,
    personType?: string,
  ) => void;
}

const useQueryOrgContacts = (): IQueryContactsHook => {
  const httpClient = useHttpClient();
  const [searchParams, setSearchParams] = useState<{
    searchTerm: string;
    limit: number;
    personType: string;
  } | null>(null);

  const { data, error, isLoading } = useInfiniteQuery({
    queryKey: ['orgContacts', searchParams?.searchTerm, searchParams?.limit, searchParams?.personType],
    queryFn: async ({ pageParam = 0 }) => {
      if (!searchParams) {
        throw new Error('Search parameters not set');
      }
      const { searchTerm, limit, personType } = searchParams;
      const params = new URLSearchParams({
        limit: limit.toString(),
        searchTerm,
        offset: pageParam.toString(),
      });
      if (personType) {
        params.append('personType', personType);
      }
      const response = await httpClient.get(`/contacts?${params.toString()}`);
      return response.data as IOrgContacts;
    },
    getNextPageParam: (lastPage: IOrgContacts, allPages) => {
      return lastPage.hasMore ? allPages.reduce((total, page) => total + page.items.length, 0) : undefined;
    },
    enabled: searchParams !== null,
    staleTime: 0,
    cacheTime: 0,
  });

  const getContacts = useCallback((searchTerm: string, limit: number = 50, personType: string = '') => {
    setSearchParams({ searchTerm, limit, personType });
  }, []);

  // Only get the first page data since we're not actually paginating
  const firstPage = data?.pages[0];

  const contacts: IOrgContacts | undefined = firstPage ? {
    count: firstPage.count,
    offset: firstPage.offset,
    limit: firstPage.limit,
    hasMore: firstPage.hasMore, // Use API's hasMore directly
    requestedFields: firstPage.requestedFields,
    items: firstPage.items,
  } : undefined;

  return {
    contactsSearchResults: {
      called: searchParams !== null,
      loading: isLoading,
      error: error as Error,
      contacts,
    },
    getContacts,
  };
};

export default useQueryOrgContacts;
